import React, { useMemo } from 'react';

export const SheetPreview = ({ data }) => {
  const model = useMemo(() => {
    if (!data) return null;
    const { sheet, styles } = data;
    const hiddenR = new Set(sheet.hiddenRows);
    const hiddenC = new Set(sheet.hiddenCols);
    const cols = [];
    for (let c = 1; c <= sheet.cols; c++) if (!hiddenC.has(c)) cols.push(c);
    const rows = [];
    for (let r = 1; r <= Math.min(sheet.rows, 1500); r++) if (!hiddenR.has(r)) rows.push(r);
    const cellMap = new Map();
    for (const [r, c, disp, si, raw] of sheet.cells) cellMap.set(`${r},${c}`, { disp, st: styles[si], raw });
    const span = new Map();
    const covered = new Set();
    for (const [r1, c1, r2, c2] of sheet.merges) {
      const rs = rows.filter((r) => r >= r1 && r <= r2).length;
      const cs = cols.filter((c) => c >= c1 && c <= c2).length;
      if (rs === 0 || cs === 0) continue;
      span.set(`${r1},${c1}`, { rs, cs });
      for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) if (!(r === r1 && c === c1)) covered.add(`${r},${c}`);
    }
    return { cols, rows, cellMap, span, covered, colw: sheet.colw, rowh: sheet.rowh, truncated: sheet.rows > 1500 };
  }, [data]);

  if (!model) return null;
  const { cols, rows, cellMap, span, covered, colw, rowh } = model;
  return (
    <div className="overflow-auto rounded-lg border border-slate-200 bg-white" style={{ maxHeight: '70vh' }} data-testid="sheet-preview">
      <table className="border-collapse text-[11px] leading-tight" style={{ tableLayout: 'fixed' }}>
        <colgroup>{cols.map((c) => <col key={c} style={{ width: Math.max(18, (colw[String(c)] || 9.14) * 7.2) }} />)}</colgroup>
        <tbody>
          {rows.map((r) => (
            <tr key={r} style={{ height: rowh[String(r)] ? rowh[String(r)] * 1.33 : 20 }}>
              {cols.map((c) => {
                const key = `${r},${c}`;
                if (covered.has(key)) return null;
                const cell = cellMap.get(key);
                const sp = span.get(key);
                const st = cell?.st;
                const isNum = cell && cell.disp && /^[-\d.,%$ ]+$/.test(cell.disp);
                return (
                  <td key={c} rowSpan={sp?.rs} colSpan={sp?.cs}
                    className="px-1 align-middle overflow-hidden whitespace-nowrap text-ellipsis"
                    style={{
                      background: st?.bg && st.bg !== '#FFFFFF' ? st.bg : undefined,
                      fontWeight: st?.b ? 600 : 400,
                      fontStyle: st?.i ? 'italic' : undefined,
                      color: st?.fc && st.fc !== '#000000' ? st.fc : undefined,
                      textAlign: st?.al || (isNum ? 'right' : 'left'),
                      borderLeft: st?.bd & 1 ? '1px solid #94a3b8' : '1px solid #f1f5f9',
                      borderRight: st?.bd & 2 ? '1px solid #94a3b8' : '1px solid #f1f5f9',
                      borderTop: st?.bd & 4 ? '1px solid #94a3b8' : '1px solid #f1f5f9',
                      borderBottom: st?.bd & 8 ? '1px solid #94a3b8' : '1px solid #f1f5f9',
                    }}
                    title={cell?.disp || ''}
                  >{cell?.disp || ''}</td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {model.truncated && <div className="p-2 text-xs text-slate-500">Vista previa limitada a 1.500 filas. El PDF incluye la hoja completa.</div>}
    </div>
  );
};
